// BL-177 Step 2j (B3): how a conversation scrolls. Never yank the thread to the bottom while the person is reading history:
//   - opening a conversation starts at the latest message;
//   - a new message scrolls the thread down ONLY when the person is within ~80 px of the bottom (or they sent it themselves);
//   - when they are scrolled up, the thread stays put and the "↓" button appears; it becomes "↓ New message" / "↓ N new messages"
//     when messages arrive meanwhile. Pressing it jumps to the latest message.
// One hook for every channel and for group threads (they all render through IntakeSessionDetailCard).
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const NEAR_BOTTOM_PX = 80;

/** How far (px) the pane is from its bottom edge. */
export function distanceFromBottom(pane) {
    if (!pane) return 0;
    return Math.max(0, pane.scrollHeight - pane.scrollTop - pane.clientHeight);
}

export function isNearBottom(pane, threshold = NEAR_BOTTOM_PX) {
    return distanceFromBottom(pane) <= threshold;
}

/** "↓" (nothing new), "↓ New message", "↓ 3 new messages". */
export function jumpLabel(newCount) {
    if (!newCount) return '↓';
    return newCount === 1 ? '↓ New message' : `↓ ${newCount} new messages`;
}

function prefersReducedMotion() {
    try { return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true; } catch { return false; }
}

/**
 * @param {{ paneRef: { current: HTMLElement | null }, items: Array<{ id: string, direction?: string, kind?: string }>, sessionId?: string | null }} options
 * @returns {{ scrolledUp: boolean, newCount: number, onScroll: () => void, jumpToLatest: () => void }}
 */
export default function useThreadScroll({ paneRef, items, sessionId = null }) {
    const [scrolledUp, setScrolledUp] = useState(false);
    const [newCount, setNewCount] = useState(0);
    const atBottomRef = useRef(true);
    const knownRef = useRef(null); // ids of the messages already on screen
    const sessionRef = useRef(sessionId);

    const scrollToBottom = useCallback((smooth = false) => {
        const pane = paneRef.current;
        if (!pane) return;
        if (smooth && !prefersReducedMotion() && typeof pane.scrollTo === 'function') pane.scrollTo({ top: pane.scrollHeight, behavior: 'smooth' });
        else pane.scrollTop = pane.scrollHeight;
        atBottomRef.current = true;
    }, [paneRef]);

    const onScroll = useCallback(() => {
        const near = isNearBottom(paneRef.current);
        atBottomRef.current = near;
        setScrolledUp((current) => (current === !near ? current : !near));
        if (near) setNewCount((current) => (current === 0 ? current : 0));
    }, [paneRef]);

    useLayoutEffect(() => {
        const real = (Array.isArray(items) ? items : []).filter((item) => item && item.kind !== 'divider');
        const ids = new Set(real.map((item) => item.id));
        // a different conversation (or the first render): start at the latest message
        if (knownRef.current === null || sessionRef.current !== sessionId) {
            sessionRef.current = sessionId;
            knownRef.current = ids;
            setNewCount(0);
            setScrolledUp(false);
            scrollToBottom(false);
            return;
        }
        const fresh = real.filter((item) => !knownRef.current.has(item.id));
        knownRef.current = ids;
        if (fresh.length === 0) return; // a status change, a read receipt: leave the scroll position alone
        const mine = fresh.some((item) => item.direction === 'outbound');
        if (atBottomRef.current || mine) {
            scrollToBottom(false);
            setNewCount(0);
            setScrolledUp(false);
            return;
        }
        setNewCount((current) => current + fresh.filter((item) => item.direction !== 'outbound').length);
    }, [items, sessionId, scrollToBottom]);

    const jumpToLatest = useCallback(() => {
        scrollToBottom(true);
        setNewCount(0);
        setScrolledUp(false);
    }, [scrollToBottom]);

    return { scrolledUp, newCount, onScroll, jumpToLatest };
}
